import { useEffect, useRef } from "react";

import type { ClienteDaApi } from "./cliente";
import { ChaveDeWebPushSchema, TokenDePushSchema } from "./esquemas";
import { useSessao } from "./sessao";

/**
 * Notificação no aparelho: permissão, inscrição e registro do token.
 *
 * A regra que vale mais do que todas as outras aqui: **a permissão nunca é
 * pedida na abertura do jogo**. Navegador nenhum dá segunda chance. Quem recusa
 * uma vez recusa para sempre, e a caixinha do sistema não volta nem se a pessoa
 * mudar de ideia, a não ser que ela vá mexer nas configurações do site, o que
 * ninguém faz. Pedir cedo demais é a forma mais rápida de perder o canal.
 *
 * Por isso o pedido mora num momento em que o motivo é óbvio: a pessoa acabou
 * de entrar numa liga assíncrona, onde a rodada roda sozinha às 20h e quem não
 * é avisado descobre no dia seguinte. `src/screens/Ligas.tsx` faz o convite em
 * português antes de qualquer coisa do navegador aparecer, e só chama daqui
 * quando a pessoa clica em sim.
 *
 * Este arquivo não desenha nada e não sabe de tela nenhuma. Ele cuida de três
 * coisas: falar com o `PushManager`, guardar qual inscrição é deste navegador e
 * manter o servidor sabendo dela.
 */

export type ClienteDePush = Pick<ClienteDaApi, "pedir" | "pedirVazio">;

/** Onde o pedido pode parar. Cada valor tem uma tela diferente do outro lado. */
export type ResultadoDoRegistro =
  /** Inscrito e registrado no servidor. */
  | "registrada"
  /** O navegador não tem Web Push. É o caso do aplicativo nativo e do Safari antigo. */
  | "indisponivel"
  /** Este servidor não tem VAPID configurado, então não há o que oferecer. */
  | "semServidor"
  /** A pessoa disse não, agora ou antes. */
  | "negada"
  /** Havia permissão para pedir, mas não era hora de perguntar. */
  | "adiada"
  /** Rede fora, serviço de push recusando, inscrição não nasceu. */
  | "falhou";

export type EstadoDaPermissao = "indisponivel" | "perguntar" | "concedida" | "negada";

/**
 * O token deste navegador, guardado para poder ser apagado na saída.
 *
 * Fica em `localStorage` porque precisa sobreviver ao fechamento da aba: quem
 * sai da conta na segunda-feira tem que deixar de receber a notificação de
 * quarta, e a inscrição do navegador não morre sozinha.
 */
const CHAVE_DO_TOKEN = "lm:push:token";

/** Marca que o convite já foi feito neste navegador, para não insistir. */
const CHAVE_DO_CONVITE = "lm:push:convidado";

// ---------------------------------------------------------------------------
// O que este navegador consegue
// ---------------------------------------------------------------------------

/**
 * Web Push completo, e não só metade dele.
 *
 * As três peças precisam existir juntas: o service worker é quem recebe a
 * mensagem com o jogo fechado, o `PushManager` é quem cria a inscrição e o
 * `Notification` é quem desenha o aviso. A WebView do aplicativo nativo tem a
 * primeira e não tem as outras duas, e é justamente ali que uma checagem pela
 * metade quebraria.
 */
export function suportaPush(): boolean {
  if (typeof navigator === "undefined" || typeof window === "undefined") return false;
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    typeof Notification !== "undefined"
  );
}

export function estadoDaPermissao(): EstadoDaPermissao {
  if (!suportaPush()) return "indisponivel";
  switch (Notification.permission) {
    case "granted":
      return "concedida";
    case "denied":
      return "negada";
    default:
      return "perguntar";
  }
}

/**
 * Vale a pena oferecer o aviso agora?
 *
 * Só quando o navegador aceita, a pessoa ainda não decidiu nada e o convite
 * ainda não foi feito neste navegador. Quem já disse não uma vez não é
 * perguntado de novo: a caixinha do sistema nem apareceria, e o convite viraria
 * um cartão que não faz nada.
 */
export function valeConvidar(): boolean {
  return estadoDaPermissao() === "perguntar" && !jaConvidamos();
}

export function jaConvidamos(): boolean {
  return ler(CHAVE_DO_CONVITE) === "sim";
}

export function marcarConvite(): void {
  gravar(CHAVE_DO_CONVITE, "sim");
}

// ---------------------------------------------------------------------------
// Registro
// ---------------------------------------------------------------------------

export interface OpcoesDoRegistro {
  /**
   * Mostrar a caixinha do sistema quando a permissão ainda não foi decidida.
   *
   * Falso é o caminho de fundo: reconfirma no servidor a inscrição de quem já
   * tinha dito sim, sem nunca fazer aparecer nada na tela de quem não disse.
   */
  pedirPermissao?: boolean;
}

/**
 * Inscreve este navegador e registra o token no servidor.
 *
 * A ordem importa: a chave do servidor é conferida **antes** de a caixinha do
 * sistema subir. Um servidor sem VAPID configurado não teria como mandar nada,
 * e gastar o único pedido de permissão da vida deste navegador num canal que
 * não existe seria queimar o canal de graça.
 */
export async function registrarPush(
  cliente: ClienteDePush,
  opcoes: OpcoesDoRegistro = {},
): Promise<ResultadoDoRegistro> {
  if (!suportaPush()) return "indisponivel";

  const permissao = estadoDaPermissao();
  if (permissao === "negada") return "negada";
  // Sem permissão e sem ordem para pedir, não há o que fazer, nem por que
  // acordar o servidor por uma chave que ninguém vai usar. É o caso mais comum
  // de todos: a passagem de fundo a cada entrada na conta.
  if (permissao === "perguntar" && !opcoes.pedirPermissao) return "adiada";

  const chave = await chavePublicaDoServidor(cliente);
  if (!chave) return "semServidor";

  if (permissao === "perguntar") {
    marcarConvite();
    const resposta = await Notification.requestPermission().catch(() => "denied" as const);
    if (resposta !== "granted") return "negada";
  }

  try {
    const inscricao = await inscrever(chave);
    if (!inscricao) return "falhou";

    const token = JSON.stringify(inscricao);
    await cliente.pedir(
      {
        metodo: "POST",
        caminho: "/v1/notifications/tokens",
        corpo: { platform: "web", token },
      },
      TokenDePushSchema,
    );
    gravar(CHAVE_DO_TOKEN, token);
    return "registrada";
  } catch {
    // Falha de rede ou recusa do serviço de push não é assunto de quem joga: a
    // central de notificações continua de pé e o aviso chega ao abrir o jogo.
    return "falhou";
  }
}

/**
 * Desfaz tudo ao sair da conta.
 *
 * Os dois lados são necessários e nesta ordem. Apagar no servidor primeiro
 * garante que a próxima rodada não mande nada para cá mesmo se o navegador
 * demorar a cancelar; cancelar a inscrição depois é o que impede o aparelho de
 * continuar acordando à toa.
 *
 * Nada aqui pode lançar: uma pessoa que clicou em sair não pode ficar presa na
 * conta porque o serviço de push não respondeu.
 */
export async function cancelarPush(cliente: ClienteDePush): Promise<void> {
  const token = ler(CHAVE_DO_TOKEN);
  apagar(CHAVE_DO_TOKEN);

  if (token) {
    await cliente
      .pedirVazio({
        metodo: "DELETE",
        // O token da web é a inscrição inteira em JSON, com barras e dois
        // pontos dentro. Sem escapar, ele viraria vários pedaços de caminho.
        caminho: `/v1/notifications/tokens/${encodeURIComponent(token)}`,
      })
      .catch(() => undefined);
  }

  if (!suportaPush()) return;
  try {
    const registro = await navigator.serviceWorker.getRegistration();
    const inscricao = await registro?.pushManager.getSubscription();
    await inscricao?.unsubscribe();
  } catch {
    // Sem service worker no ar não há inscrição para cancelar.
  }
}

/**
 * A chave pública do Web Push deste servidor.
 *
 * Vem do servidor e não de uma variável da build de propósito: a chave privada
 * que combina com ela mora no worker, e as duas precisam ser o mesmo par. Duas
 * configurações separadas para a mesma coisa quebrariam em silêncio na primeira
 * vez que alguém trocasse uma e esquecesse a outra.
 */
export async function chavePublicaDoServidor(cliente: ClienteDePush): Promise<string | null> {
  try {
    const resposta = await cliente.pedir(
      { metodo: "GET", caminho: "/v1/notifications/vapid" },
      ChaveDeWebPushSchema,
    );
    return resposta.publicKey;
  } catch {
    return null;
  }
}

/** A inscrição, como ela é gravada em `push_tokens`. */
export interface InscricaoDePush {
  endpoint: string;
  keys: { p256dh: string; auth: string };
}

/**
 * Pega a inscrição que já existe, ou cria uma.
 *
 * Reaproveitar importa: cada `subscribe` novo invalida o anterior, e uma
 * inscrição nova a cada abertura deixaria uma trilha de tokens mortos no banco,
 * que só sumiriam quando o serviço de push recusasse cada um deles.
 */
export async function inscrever(chavePublica: string): Promise<InscricaoDePush | null> {
  const registro = await navigator.serviceWorker.ready;
  const existente = await registro.pushManager.getSubscription();
  const inscricao =
    existente ??
    (await registro.pushManager.subscribe({
      /*
       * Obrigatório nos navegadores: prometer que toda mensagem recebida vira
       * um aviso visível. Push silencioso serviria para rastrear gente, e é por
       * isso que ninguém mais aceita.
       */
      userVisibleOnly: true,
      applicationServerKey: chaveParaBytes(chavePublica),
    }));

  return normalizar(inscricao);
}

/**
 * Reduz a inscrição ao que o servidor precisa.
 *
 * `toJSON()` do navegador traz também `expirationTime`, que é quase sempre
 * nulo e não serve para cifrar nada. Como a inscrição inteira é gravada como
 * texto na coluna `token`, cada campo a mais é caractere a mais num valor que
 * já é grande.
 */
export function normalizar(inscricao: PushSubscription): InscricaoDePush | null {
  const bruta = inscricao.toJSON() as {
    endpoint?: string;
    keys?: { p256dh?: string; auth?: string };
  };
  const endpoint = bruta.endpoint;
  const p256dh = bruta.keys?.p256dh;
  const auth = bruta.keys?.auth;
  if (!endpoint || !p256dh || !auth) return null;
  return { endpoint, keys: { p256dh, auth } };
}

/**
 * A chave do VAPID vira bytes.
 *
 * O servidor entrega em base64url, que é como a chave circula em todo lugar, e
 * o `PushManager` só aceita bytes crus. O `atob` do navegador não entende
 * base64url, então os dois caracteres trocados voltam ao que eram e o
 * preenchimento é reposto.
 */
export function chaveParaBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const preenchimento = "=".repeat((4 - (base64url.length % 4)) % 4);
  const base64 = (base64url + preenchimento).replace(/-/g, "+").replace(/_/g, "/");
  const cru = atob(base64);
  // Bloco próprio, e não o padrão: o `PushManager` recusa bytes apoiados num
  // bloco que possa ser compartilhado.
  const bytes = new Uint8Array(new ArrayBuffer(cru.length));
  for (let indice = 0; indice < cru.length; indice++) bytes[indice] = cru.charCodeAt(indice);
  return bytes;
}

// ---------------------------------------------------------------------------
// Armazenamento local
// ---------------------------------------------------------------------------

/*
 * Tudo em try/catch porque `localStorage` não é garantido: janela anônima,
 * dados do site bloqueados e WebView com armazenamento desligado jogam ao ler e
 * ao escrever. Nenhuma dessas situações pode derrubar o jogo, e a única coisa
 * que se perde é a lembrança de que o convite já foi feito.
 */
function ler(chave: string): string | null {
  try {
    return localStorage.getItem(chave);
  } catch {
    return null;
  }
}

function gravar(chave: string, valor: string): void {
  try {
    localStorage.setItem(chave, valor);
  } catch {
    // Sem lugar para guardar. O registro no servidor já aconteceu.
  }
}

function apagar(chave: string): void {
  try {
    localStorage.removeItem(chave);
  } catch {
    // Igual ao de cima: nada a fazer, e nada a quebrar.
  }
}

// ---------------------------------------------------------------------------
// O elo com a sessão
// ---------------------------------------------------------------------------

/**
 * Mantém o token de push alinhado com quem está logado.
 *
 * Não desenha nada e nunca pede permissão. Faz as duas pontas que nenhuma tela
 * poderia fazer sozinha, porque elas acontecem fora de qualquer tela:
 *
 * Ao **entrar**, reconfirma no servidor a inscrição que este navegador já tinha.
 * É o que faz o token trocar de dono quando duas pessoas usam o mesmo aparelho,
 * e o que ressuscita o registro de quem teve o token apagado por uma rodada em
 * que o serviço de push estava fora do ar.
 *
 * Ao **sair**, apaga o token e cancela a inscrição. Sem isso, quem saiu da conta
 * continuaria recebendo o aviso de rodada de uma liga que não é mais dele,
 * inclusive no aparelho que passou para outra pessoa.
 */
export function SincroniaDePush(): null {
  const sessao = useSessao();
  const cliente = sessao.cliente;
  const conectada = sessao.estado === "conectada";
  const estavaConectada = useRef(false);

  useEffect(() => {
    if (conectada) {
      estavaConectada.current = true;
      void registrarPush(cliente);
      return;
    }

    // Só ao sair de uma sessão de verdade. Sem esta guarda, a primeira
    // renderização de quem chega como visitante já mandaria um DELETE.
    if (!estavaConectada.current) return;
    estavaConectada.current = false;
    void cancelarPush(cliente);
  }, [conectada, cliente]);

  return null;
}
